import { amplitude } from '@/analytics/amplitude';
import { attachPendo, initializePendo } from '@/analytics/pendo';
import { PLAY_FIRST_SOURCE, PLAY_SOURCES } from '@/analytics/constants/amplitude-users.constants';

import { KeyValues } from '@/types/core-types';
import { logger } from '@/shared/logging';
import { State } from '@/store/play-root-state';
import { watch } from 'vue';
import { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import { AccountInfo, UserRoleState } from '@/store/auth/types';

const log = logger('AnalyticsHandler');

export function watchAnalytics(router: Router, state: State) {
    watch([() => router.currentRoute.value, () => state.auth.session?.coreAppId, () => state.auth.user], ([route, appId, user]) => {
        const params:KeyValues = {
            ...route.query,
            ...route.params,
        };

        const { playSource, amplitudeSessionId } = params;

        if (playSource) {
            amplitude.setOnce(PLAY_FIRST_SOURCE, playSource);
            amplitude.postInsert(PLAY_SOURCES, playSource);
        }

        if (amplitudeSessionId) {
            log.info('Changing amplitude sessionID', amplitudeSessionId, appId, user);
            // eslint-disable-next-line radix
            amplitude.setSessionId(parseInt(amplitudeSessionId));
        }
    });
}

const isCoachPortalRoute = (route: RouteLocationNormalizedLoaded) => route.matched.some(({ meta }) => Boolean(meta.coachPortal));

// Lower case with spaces, per the Pendo property conventions in keap-web ADR-008.
const partnerRole = ({ isAdmin, isCoachAdmin, isCoach }: UserRoleState) => {
    if (isAdmin) return 'admin';

    if (isCoachAdmin) return 'coach admin';

    return isCoach ? 'coach' : 'none';
};

// A partner works across many client tenants, so the Pendo account is their own
// app rather than whichever client they happen to be looking at.
const partnerAccountId = (accounts: AccountInfo) => accounts?.apps?.[0]?.account?.legacyId;

/**
 * Loads Pendo and identifies the partner, but only inside the Coaching Portal —
 * elsewhere play-web runs embedded in hosts that report to Pendo themselves.
 *
 * Pendo ignores every `initialize` after the first, so this waits for the
 * account details that arrive asynchronously after login, then identifies once.
 */
export function watchPendo(router: Router, state: State) {
    let identified = false;

    watch([
        () => router.currentRoute.value,
        () => state.auth.user,
        () => state.auth.accounts,
    ], ([route, user, accounts]) => {
        if (identified || !isCoachPortalRoute(route) || !user?.id || !accounts) {
            return;
        }

        identified = true;
        attachPendo();

        const accountId = partnerAccountId(accounts);

        initializePendo({
            visitor: {
                id: user.id,
                email: user.username,
                firstName: user.givenName,
                lastName: user.familyName,
                role: partnerRole(state.auth.roles),
            },
            ...(accountId ? { account: { id: accountId } } : {}),
        });
    }, { immediate: true });
}
